import ImageIcon from '@mui/icons-material/Image'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { CSSProperties, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ImageViewerDialog from 'components/ImageViewerDialog'
import { notificationApi } from 'hooks/api/notification'
import { useSnackbar } from 'hooks/useSnackbar/SnackbarContext'
import { NotificationFileInfo } from 'types/notification'

interface Props {
  files: NotificationFileInfo[]
}

const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg']

const isImageFile = (filename: string): boolean => {
  const ext = filename.split('.').pop()?.toLowerCase() || ''
  return imageExtensions.includes(ext)
}

export default function NotificationFilesComponent({ files }: Props) {
  const { t } = useTranslation('common')
  const { showSnackbar } = useSnackbar()
  const [fileUrls, setFileUrls] = useState<Record<string, string>>({})
  const [loadingFiles, setLoadingFiles] = useState<Set<string>>(new Set())
  const [openingFiles, setOpeningFiles] = useState<Set<string>>(new Set())
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  useEffect(() => {
    const fetchUrls = async () => {
      const imageFiles = files.filter((f) => isImageFile(f.filename))

      // 未取得のファイルのみ抽出
      const unfetchedFiles = imageFiles.filter(
        (file) => !fileUrls[file.notificationFileId],
      )

      if (unfetchedFiles.length === 0) return

      // ローディング状態を一括設定
      setLoadingFiles((prev) => {
        const next = new Set(prev)
        unfetchedFiles.forEach((file) => next.add(file.notificationFileId))
        return next
      })

      // 並列で取得
      const results = await Promise.allSettled(
        unfetchedFiles.map((file) =>
          notificationApi()
            .findFileUrlById(file.notificationFileId)
            .then((res) => ({
              fileId: file.notificationFileId,
              url: res.data.url,
            })),
        ),
      )

      // 結果を反映
      const newUrls: Record<string, string> = {}
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          newUrls[result.value.fileId] = result.value.url
        } else {
          console.error('ファイルURL取得失敗:', result.reason)
        }
      })

      setFileUrls((prev) => ({ ...prev, ...newUrls }))

      // ローディング状態を一括解除
      setLoadingFiles((prev) => {
        const next = new Set(prev)
        unfetchedFiles.forEach((file) => next.delete(file.notificationFileId))
        return next
      })
    }

    fetchUrls()
  }, [files])

  if (!files || files.length === 0) {
    return null
  }

  const imageFiles = files.filter((f) => isImageFile(f.filename))
  const otherFiles = files.filter((f) => !isImageFile(f.filename))

  const handleOpenFile = async (file: NotificationFileInfo) => {
    setOpeningFiles((prev) => new Set(prev).add(file.notificationFileId))

    try {
      const res = await notificationApi().findFileUrlById(
        file.notificationFileId,
      )
      // presigned urlに余計なクエリパラメータを混ぜられないため
      // リダイレクトページ経由でブラウザで開く
      location.href = `https://kumocan.com/redirect?url=${encodeURIComponent(res.data.url)}&launch_app=true`
    } catch (error) {
      console.error('ファイルURL取得失敗:', file.notificationFileId, error)
      showSnackbar(t('notification_files.open_failed'), 'error')
    } finally {
      setOpeningFiles((prev) => {
        const next = new Set(prev)
        next.delete(file.notificationFileId)
        return next
      })
    }
  }

  const handleClickImage = (url: string) => () => {
    setSelectedImage(url)
  }

  const handleCloseImageViewer = () => {
    setSelectedImage(null)
  }

  return (
    <Box sx={containerStyle}>
      {/* ヘッダー */}
      <Typography style={sectionHeaderStyle}>
        {t('notification_files.related_docs', { count: files.length })}
      </Typography>

      {/* 画像ファイル */}
      {imageFiles.length > 0 && (
        <Box sx={imageFilesContainerStyle}>
          <Typography sx={imageFilesLabelStyle}>
            {t('notification_files.image_files', { count: imageFiles.length })}
          </Typography>
          <Box sx={imageGridStyle}>
            {imageFiles.map((file) => {
              const url = fileUrls[file.notificationFileId]
              const isLoading = loadingFiles.has(file.notificationFileId)

              return (
                <Box key={file.notificationFileId} sx={imageBoxStyle}>
                  {isLoading ? (
                    <CircularProgress size={40} />
                  ) : url ? (
                    <img
                      src={url}
                      alt={file.filename}
                      style={imageStyle}
                      onClick={handleClickImage(url)}
                    />
                  ) : (
                    <ImageIcon sx={imageFallbackIconStyle} />
                  )}
                </Box>
              )
            })}
          </Box>
        </Box>
      )}

      {/* その他のファイル */}
      {otherFiles.length > 0 && (
        <Box>
          <Typography sx={otherFilesLabelStyle}>
            {t('notification_files.other_files', { count: otherFiles.length })}
          </Typography>
          {otherFiles.map((file) => {
            const isOpening = openingFiles.has(file.notificationFileId)

            return (
              <Box key={file.notificationFileId} sx={fileItemStyle}>
                <InsertDriveFileIcon sx={fileIconStyle} />
                <Typography sx={fileNameStyle}>{file.filename}</Typography>
                <Button
                  startIcon={
                    isOpening ? (
                      <CircularProgress size={16} />
                    ) : (
                      <OpenInNewIcon />
                    )
                  }
                  onClick={() => handleOpenFile(file)}
                  disabled={isOpening}
                  sx={fileButtonStyle}
                >
                  {isOpening
                    ? t('notification_files.opening')
                    : t('notification_files.open')}
                </Button>
              </Box>
            )
          })}
        </Box>
      )}

      <ImageViewerDialog
        imageUrl={selectedImage}
        onClose={handleCloseImageViewer}
      />
    </Box>
  )
}

const containerStyle: CSSProperties = {
  marginTop: '20px',
}

const sectionHeaderStyle: CSSProperties = {
  fontWeight: 'bold',
  fontSize: '12px',
  marginTop: '20px',
  marginBottom: '10px',
}

const imageFilesContainerStyle: CSSProperties = {
  marginBottom: '20px',
}

const imageFilesLabelStyle: CSSProperties = {
  fontSize: '11px',
  color: '#666',
  marginBottom: '10px',
}

const imageGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: '10px',
}

const imageBoxStyle: CSSProperties = {
  aspectRatio: '1 / 1',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  borderRadius: '4px',
}

const imageStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  cursor: 'pointer',
}

const imageFallbackIconStyle: CSSProperties = {
  fontSize: 40,
  color: '#999',
}

const otherFilesLabelStyle: CSSProperties = {
  fontSize: '11px',
  color: '#666',
  marginBottom: '10px',
}

const fileItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '12px 16px',
  marginBottom: '8px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  backgroundColor: '#fff',
}

const fileIconStyle: CSSProperties = {
  fontSize: 32,
  color: '#999',
}

const fileNameStyle: CSSProperties = {
  fontSize: '14px',
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

const fileButtonStyle: CSSProperties = {
  fontSize: '14px',
  textTransform: 'none',
  minWidth: 'auto',
  padding: '4px 8px',
}
